import {
    init,
    browserTracingIntegration,
    captureException,
    captureMessage,
    consoleLoggingIntegration,
    getCurrentScope,
    metrics,
    replayIntegration,
    startInactiveSpan,
    withScope,
} from "@sentry/gatsby"

import { getVersionNumber } from "~utils/helpers/helpers"
import {
    getAllowUrlsPatterns,
    getApiUrls,
    isFirstPartyApiUrl,
    setSentryClient,
} from "~utils/sentry"

setSentryClient({
    startInactiveSpan,
    withScope,
    captureException,
    captureMessage,
    getCurrentScope,
    metrics,
})

init({
    dsn: process.env.GATSBY_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    enabled: process.env.GATSBY_IS_SENTRY_DISABLED !== "true",
    release: getVersionNumber(),
    enableLogs: true,
    tracePropagationTargets: getApiUrls(),
    integrations: [
        browserTracingIntegration({
            shouldCreateSpanForRequest: (url) => isFirstPartyApiUrl(url),
        }),
        replayIntegration(),
        consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],

    tracesSampler: ({ name }) => {
        // Drop health-check polling from traces — high volume, zero diagnostic value
        if (name.includes("/health")) return 0
        return 0.1
    },

    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // ignoreErrors filters by error *message* — suppresses noisy browser quirks
    ignoreErrors: [
        /ResizeObserver loop limit exceeded/i,
        /Non-Error promise rejection captured/i,
    ],

    // allowUrls filters by *source URL* — only accept events originating from
    // our own domain or API hosts, preventing noise from browser extensions and
    // injected third-party scripts. This complements ignoreErrors above.
    allowUrls: [
        /^https?:\/\/(?:[^/]+\.)*mallardbay\.com(\/|$)/,
        ...getAllowUrlsPatterns(),
    ],
    denyUrls: [/extensions\//i, /chrome-extension:\/\//i],

    beforeBreadcrumb(breadcrumb) {
        const url = breadcrumb.data?.url
        if (typeof url === "string" && !isFirstPartyApiUrl(url)) return null
        return breadcrumb
    },

    beforeSend(event) {
        const frames = event.exception?.values?.[0]?.stacktrace?.frames
        const lastFrame = frames?.length ? frames[frames.length - 1] : undefined
        const file = lastFrame?.filename
        if (!file) return event
        if (!/^https?:\/\//.test(file)) return event
        if (/mallardbay\.com/.test(file)) return event
        if (isFirstPartyApiUrl(file)) return event
        return null
    },
})
